import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { queryClient } from "@/lib/queryClient";
import { isStandalone, isPushSupported, enablePush } from "@/lib/webPush";

const DISMISSED_AT_KEY = "edufocal-push-promo-dismissed-at";
const REMIND_DAYS = 7;

// Shown inside the installed app (standalone mode) when notification
// permission has not been asked yet. If the user ever denies permission we
// never bring it up again; the Settings page explains how to unblock.
export function PushPermissionModal() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isStandalone() || !isPushSupported()) return;
    if (Notification.permission !== "default") return;
    if (!import.meta.env.VITE_VAPID_PUBLIC_KEY) return;
    const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY) || 0);
    if (dismissedAt && Date.now() < dismissedAt + REMIND_DAYS * 24 * 60 * 60 * 1000) return;

    const timer = setTimeout(() => setOpen(true), 2000);
    return () => clearTimeout(timer);
  }, [user?.id]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setOpen(false);
  };

  const turnOn = async () => {
    setEnabling(true);
    try {
      const result = await enablePush();
      if (result === "granted") {
        queryClient.invalidateQueries({ queryKey: ["/api/push/prefs"] });
        toast({
          title: "Notifications are on!",
          description: "We'll let you know when something new happens.",
        });
      }
      // Denied: close quietly and never nag again (permission gate handles it)
      setOpen(false);
    } catch (err) {
      console.error("[WebPush] Enable failed:", err);
      setOpen(false);
    } finally {
      setEnabling(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="max-w-sm w-full p-0 overflow-hidden gap-0 flex flex-col">
        <div className="bg-indigo-600 px-6 pt-8 pb-6 text-center text-white">
          <div className="text-5xl mb-3">🔔</div>
          <h2 className="text-xl font-bold leading-tight">Turn on notifications?</h2>
          <p className="text-sm text-white/85 mt-2">
            Know right away when you get a new message, when something cool happens on EduFocal, and when it's time to practice.
          </p>
        </div>

        <div className="px-6 py-5">
          <p className="text-sm text-muted-foreground">
            You're in charge: pick exactly which alerts you want in Settings, anytime.
          </p>
        </div>

        <div className="px-6 pb-6 space-y-2">
          <Button
            onClick={turnOn}
            disabled={enabling}
            className="w-full bg-indigo-600 hover:bg-indigo-700"
            data-testid="button-push-enable"
          >
            {enabling ? "One moment..." : "Turn on notifications"}
          </Button>
          <Button
            variant="ghost"
            onClick={dismiss}
            className="w-full"
            data-testid="button-push-later"
          >
            Maybe later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
